// Copyright (c) StrangeBee 2024

import { classList, getNestedObject } from '@Helpers/';
import React, { ReactElement, ReactNode } from 'react';
import InfoDetail from '@Atoms/InfoDetail';

import './Details.scss';

export type ExtractProps = {
    name?: ((...args: any[]) => ReactNode) | ReactNode;
    render?: (...args: any[]) => any;
    isForce?: boolean;
    className?: string;
    group?: string;
    hidden?: boolean;
};

type DetailsProps = {
    data: any;
    extract: Record<string, ExtractProps>;
    className?: string;
    forbidden?: string[];
};

type TypeEnum = 'array' | 'object' | 'boolean' | 'number' | 'bigint' | 'default' | 'null';

const thereSomeThing: Record<TypeEnum, (...args: any[]) => any> = {
    array: (val: any[]): number => val.length,
    object: (val: any): number => Object.keys(val).length,
    boolean: (): boolean => true,
    number: (val: number): number => val,
    bigint: (val: number): number => val,
    null: (): boolean => false,
    default: (val: any) => val,
};

function isNotEmpty(value: any): boolean {
    let type: TypeEnum = typeof value as TypeEnum;
    if (type === 'object' && Array.isArray(value)) {
        type = 'array';
    }
    if (type === 'object' && value === null) {
        type = 'null';
    }
    return (thereSomeThing[type] || thereSomeThing.default)(value);
}

const Details = ({ data, extract, className, forbidden = [] }: DetailsProps): ReactElement => {
    const extracted = Object.entries(extract);

    const childrens = extracted.reduce((acc, [path, extractOption]): ReactElement[] => {
        const { name, isForce = false, className = '', hidden = false } = extractOption;

        if (forbidden.includes(path)) {
            return acc;
        }

        const value = getNestedObject(data, path) ?? 0;

        return !hidden && (isNotEmpty(value) || isForce)
            ? [
                  ...acc,
                  <InfoDetail
                      className={classList('detail__item', className)}
                      key={`${path}`}
                      name={name}
                      value={value}
                      data={data}
                      render={extractOption.render}
                  />,
              ]
            : acc;
    }, [] as any[]);

    return <div className={classList('detail__list', className)}>{childrens}</div>;
};

export default Details;
